import { useEffect, useRef } from 'react';
import { useQuery, type QueryKey } from '@tanstack/react-query';
import { useStore } from '@nanostores/react';
import { billing } from '@/services/billing';
import type { CreditCardFormAddress } from '../utils/credit-card-form-submit';
import { hasTaxableAddress, TAX_DEBOUNCED_FIELDS, TAX_LOOKUP_DEBOUNCE_MS } from '../utils/tax-lookup-rules';
import { useAbortableTaxLookup } from './use-abortable-tax-lookup';
import { sleep } from '@/utils/sleep';

type TaxLookupTarget = Pick<CreditCardFormAddress, 'country' | 'state' | 'zipCode' | 'addressLine1'>;

function taxLookupTarget(address: CreditCardFormAddress): TaxLookupTarget {
  return {
    country: address.country,
    state: address.state,
    zipCode: address.zipCode,
    addressLine1: address.addressLine1,
  };
}

// The first lookup is a chosen address, not typed, so it does not wait out the debounce.
function isTypedChange(previous: TaxLookupTarget | null, next: TaxLookupTarget): boolean {
  return previous != null && TAX_DEBOUNCED_FIELDS.some((field) => previous[field] !== next[field]);
}

function creditsPurchaseTaxRateQueryKey(target: TaxLookupTarget): QueryKey {
  return ['creditsPurchaseTaxRate', target.country, target.state, target.zipCode, target.addressLine1];
}

export type CreditsPurchaseTaxRate = {
  rate: number | undefined;
  isFetching: boolean;
};

/**
 * A rate is only for the address on screen. The debounce runs inside the query, so `isFetching` also
 * blocks submit while the user types.
 */
export function useCreditsPurchaseTaxRate(address: CreditCardFormAddress): CreditsPurchaseTaxRate {
  const taxRate = useStore(billing.taxRate);
  const target = taxLookupTarget(address);
  const isComplete = hasTaxableAddress(address);

  // React Query does not cancel a superseded run, so the abort stops an old lookup writing a stale rate.
  const lookup = useAbortableTaxLookup();
  const lastTargetRef = useRef<TaxLookupTarget | null>(null);

  const queryKey = creditsPurchaseTaxRateQueryKey(target);
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      // Here, not in an effect: a deduped key must not cancel the lookup it waits on.
      const signal = lookup.begin(queryKey);

      const isTyped = isTypedChange(lastTargetRef.current, target);
      lastTargetRef.current = target;
      if (isTyped) {
        await sleep(TAX_LOOKUP_DEBOUNCE_MS, signal);
        if (signal.aborted) {
          return null;
        }
      }

      await billing.updateTaxRate.perform({ ...target, signal });
      // A superseded run skips its write, so the store holds the current rate.
      return billing.taxRate.get()?.rate ?? null;
    },
    enabled: isComplete,
    staleTime: 0,
    gcTime: 0,
  });

  // Abort first, or the old lookup writes its rate back after the reset.
  useEffect(() => {
    if (!isComplete) {
      lookup.abort();
      billing.resetTaxRate();
    }
  }, [isComplete, lookup]);
  useEffect(
    () => () => {
      lookup.abort();
      billing.resetTaxRate();
    },
    [lookup],
  );

  // Do not wait for the reset effect: the totals must never show tax for an untaxable address.
  return { rate: isComplete ? taxRate?.rate : undefined, isFetching: isComplete && query.isFetching };
}
